import { SheetModal, UnsavedChangesDialog } from '..';
import { EditCareAssumptionsForm } from './EditCareAssumptionsForm';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetClientUpdatesStatus,
  selectClient,
  selectClientUpdatesStatus,
} from '../model';

type EditCareAssumptionsProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export function EditCareAssumptionsModal({
  open,
  setOpen,
}: EditCareAssumptionsProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const clientStatus = useSelector(selectClientUpdatesStatus);
  const {
    intakeSurvey: { clientHasStartedLtc },
  } = client;
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] =
    useState(false);

  const onClose = () => {
    if (unsavedChanges) {
      setShowUnsavedChangesWarning(true);
    } else {
      setOpen(false);
      setUnsavedChanges(false);
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedChangesWarning(false);
    setOpen(false);
    setUnsavedChanges(false);
  };

  const handleCancelClose = () => {
    setShowUnsavedChangesWarning(false);
  };

  // show complete/error toast side effect
  useEffect(() => {
    const isLtcAtAgeComplete =
      clientHasStartedLtc || clientStatus.ltcAtAge === 'complete';
    const isPhaseComplete = clientStatus.phaseDurations === 'complete';
    const isInflationComplete =
      clientStatus.healthCareInflationRate === 'complete';

    if (isLtcAtAgeComplete && isPhaseComplete && isInflationComplete) {
      setOpen(false);

      // Reset all statuses
      if (!clientHasStartedLtc) {
        dispatch(resetClientUpdatesStatus('ltcAtAge'));
      }
      dispatch(resetClientUpdatesStatus('healthCareInflationRate'));
      dispatch(resetClientUpdatesStatus('phaseDurations'));
    }
  }, [clientStatus, clientHasStartedLtc]);

  return (
    <>
      <SheetModal
        title="Edit Care Assumptions"
        subTitle="Edit the assumptions that influence your cost"
        open={open}
        onClose={onClose}
        modal={true}
        className="md:min-w-[550px]"
        closeButtonTestId="edit-care-assumptions-close-button"
      >
        <div
          className="my-6"
          data-testid="edit-care-assumptions-modal"
        >
          <EditCareAssumptionsForm
            open={open}
            setUnsavedChanges={setUnsavedChanges}
          />
        </div>
      </SheetModal>
      <UnsavedChangesDialog
        showWarning={showUnsavedChangesWarning}
        handleCancelClose={handleCancelClose}
        handleConfirmClose={handleConfirmClose}
        dialogTitle="Unsaved Changes"
        dialogDescription="You have unsaved changes. Are you sure you want to close without saving?"
        data-testid="edit-care-assumptions-unsaved-changes-dialog"
        cancelButtonTestId="edit-care-assumptions-unsaved-changes-cancel"
        confirmButtonTestId="edit-care-assumptions-unsaved-changes-confirm"
      />
    </>
  );
}
